import ModalCustom from "components/ModalCustom"
import ActionFooterModalCard from "components/ModalCustom/ActionFooterModalCard"
import { useEffect, useState, useCallback } from "react"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { MESSENGER_NOTIFICATION } from "constants/messenger"
import Tippy from "@tippyjs/react"
import IconInfoCircle from "assets/images/icons/icon-info-circle.svg"
import InputDefault from "components/Input/InputDefault"
import { changeLabelStatusEmail, getPrivateKeyByUserId } from "helpers/utils"
import FormInputCard from "pages/auth/molecules/FormInputCard"
import { useString } from "helpers/hooks"
import { STATUS_INPUT } from "components/Input/types"
import {
  postInviteMemberMiddleware,
  checkEmailExistsMiddleware,
} from "../api.service"
import { getShareKeyProjectMiddleware } from "pages/project-share-organization/project-share-organization.api"
import { ShareKeyProject } from "pages/project-share-organization/ShareKeyOrganizationPage"
import { EncryptionKeys } from "controllers/EncryptionController"
import { EncryptionKeyInput, KeyEncryptionType } from "types"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { includes, debounce } from "lodash"
import forge from "node-forge"
import {
  addRoleGroupOrganizationMiddleware,
  createEncryptionKeysMiddleware,
} from "pages/projects/services/api"
import { configureStore } from "stores/configureStore"
import { closeLoading, startLoading } from "reducers/loading"

interface Props {
  isOpen: boolean
  roles: { id: string; name: string; description: string }[]
  onCloseModal: () => void
  onSuccess: () => void
}

export const InviteMemberModal = (props: Props) => {
  const { roles, onCloseModal, onSuccess, isOpen } = props

  const [selectedRole, setSelectedRole] = useState<string>("")
  const email = useString("")
  const [statusInputEmail, setStatusInputEmail] = useState({
    status: STATUS_INPUT.DEFAULT,
    label: "",
  })

  const checkEmailExists = useCallback(
    debounce(async (emailValue: string) => {
      try {
        const result = await checkEmailExistsMiddleware(emailValue)
        if (result) {
          if (result.existInWorkspace) {
            setStatusInputEmail({
              status: STATUS_INPUT.ERROR,
              label: "This email already exists in the workspace.",
            })
            return
          } else if (result.existInSystem) {
            setStatusInputEmail({
              status: STATUS_INPUT.VALID,
              label: "This email is registered in the system.",
            })
          } else {
            setStatusInputEmail({
              status: STATUS_INPUT.ERROR,
              label: "This email is not registered in the system.",
            })
          }
        }
      } catch (error) {
        // Handle error silently or show a message
      }
    }, 500),
    []
  )

  useEffect(() => {
    if (isOpen) {
      setSelectedRole("")
      email.setValue("")
      setStatusInputEmail({
        status: STATUS_INPUT.DEFAULT,
        label: "",
      })
      // Cancel pending debounced calls when modal closes
      checkEmailExists.cancel()
    }
    // Cleanup: cancel pending debounced calls on unmount
    return () => {
      checkEmailExists.cancel()
    }
  }, [isOpen, checkEmailExists])

  const handleChangeItem = (id: string) => () => {
    if (selectedRole === id) {
      setSelectedRole("")
      return
    }
    setSelectedRole(id)
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    email.setValue(value)
    const emailStatus = changeLabelStatusEmail(value)
    setStatusInputEmail(emailStatus)

    // If email is valid, call API to check if email exists (debounced)
    if (emailStatus.status === STATUS_INPUT.VALID) {
      checkEmailExists(value)
    }
  }

  const onKeyPressInput = (
    event: React.KeyboardEvent<HTMLDivElement>
  ): void => {
    if (event.key === "Enter") {
      event.preventDefault()
      if (handleDisableButtonAccount()) {
        return
      }
      onSubmit()
    }
  }

  const handleDisableButtonAccount = () => {
    const emailStatus = changeLabelStatusEmail(email.value)
    const isEmailError = emailStatus.status === STATUS_INPUT.ERROR
    if (!email.value || isEmailError) return true
    if (!selectedRole) {
      return true
    }
    return false
  }

  const onSubmit = async () => {
    configureStore.dispatch(startLoading())
    try {
      const responseInviteeMember = await postInviteMemberMiddleware(
        email.value,
        selectedRole
      )

      if (
        !responseInviteeMember.data?.data?.userId ||
        !responseInviteeMember?.data?.data?.is_share_all_project
      ) {
        toast(
          <LabelNotificationPage
            messenger={MESSENGER_NOTIFICATION.POST_INVITE_USER_SUCCESS}
            type="success"
          />
        )
        onCloseModal()
        closeLoading()
        onSuccess()
        return
      }
      if (responseInviteeMember?.data?.data?.is_share_all_project) {
        await addRoleGroupOrganizationMiddleware({
          is_share_all_project: true,
          user_ids: JSON.stringify([responseInviteeMember.data.data.userId]),
        })
        const response = await getShareKeyProjectMiddleware(
          responseInviteeMember.data.data.userId,
          true
        )

        if (!response.users?.length) {
          toast(
            <LabelNotificationPage
              messenger={MESSENGER_NOTIFICATION.POST_INVITE_USER_SUCCESS}
              type="success"
            />
          )
          onCloseModal()
          closeLoading()
          onSuccess()
          return
        }
        const decryptedKeys: ShareKeyProject[] = []
        if (response?.projectKeys && response?.projectKeys.length) {
          const userVaultKey = localStorage.getItem(
            EncryptionKeys.userVaultKey
          ) as string
          const privateKeyPem: any = await getPrivateKeyByUserId()
          for (const el of response.projectKeys) {
            let decrypted = el.encryptedKey
            if (el.type === KeyEncryptionType.ProjectShareKey) {
              decrypted = privateKeyPem.decrypt(el.encryptedKey, "RSA-OAEP")
            } else {
              decrypted = await encryptionGCMHelper.decryptContext(
                userVaultKey,
                el.encryptedKey
              )
            }
            if (decrypted && decrypted !== el.encryptedKey) {
              decryptedKeys.push({
                projectKey: decrypted,
                projectId: el.id,
              })
            }
          }
        }
        for (const user of response.users) {
          const publicKeyFromPem = forge.pki.publicKeyFromPem(user.publicKey)
          const encryptionKeys: EncryptionKeyInput[] = []

          decryptedKeys.forEach((el) => {
            if (!includes(user.projectKeyIds, el.projectId)) {
              try {
                const encryptedKey = publicKeyFromPem.encrypt(
                  el.projectKey,
                  "RSA-OAEP"
                )
                encryptionKeys.push({
                  encrypted_key: encryptedKey,
                  relation_id: el.projectId,
                  type: KeyEncryptionType.ProjectShareKey,
                  user_id: user.id,
                })
              } catch (error) { }
            }
          })
          await createEncryptionKeysMiddleware(encryptionKeys, true)
          toast(
            <LabelNotificationPage
              messenger={MESSENGER_NOTIFICATION.POST_INVITE_USER_SUCCESS}
              type="success"
            />
          )
          onCloseModal()
          closeLoading()
          onSuccess()
        }
      }
    } catch (error: any) {
      toast(
        <LabelNotificationPage
          messenger={
            error.response.data.message ||
            MESSENGER_NOTIFICATION.POST_INVITE_USER_ERROR
          }
          type="error"
        />
      )
    } finally {
      closeLoading()
    }
  }

  const renderBody = () => {
    return (
      <div className="flex flex-col">
        <FormInputCard title="Email" required>
          <InputDefault
            value={email.value}
            onChange={handleEmailChange}
            status={statusInputEmail.status}
            labelStatus={statusInputEmail.label}
            onKeyPress={onKeyPressInput}
          />
        </FormInputCard>
        <FormInputCard title="Role" required>
          <div className="grid grid-cols-2 gap-2">
            {roles.map((role) => {
              const isActive = role.id === selectedRole
              return (
                <div
                  key={role.id}
                  className="rounded px-1 cursor-pointer min-h-[32px] flex items-center"
                  style={{
                    border: isActive
                      ? "1px solid #F7AC1B"
                      : "1px solid #E4E4E4",
                    boxShadow: isActive
                      ? "0px 0px 0px 4px rgba(247, 172, 27, 0.1)"
                      : "none",
                    backgroundColor: isActive ? "#FFFFFF" : "#F7F7F7",
                  }}
                  onClick={handleChangeItem(role.id)}
                >
                  <p
                    className="font-normal text-center w-full"
                    style={{
                      color: isActive ? "#222222" : "#7A7A7A",
                      fontSize: 14,
                      lineHeight: "24px",
                    }}
                  >
                    {role.name}
                  </p>
                  <Tippy content={role.description}>
                    <img
                      src={IconInfoCircle}
                      alt="icon"
                      className="cursor-pointer pl-2"
                    />
                  </Tippy>
                </div>
              )
            })}
          </div>
        </FormInputCard>

        <ActionFooterModalCard
          onCloseModal={onCloseModal}
          onSubmit={onSubmit}
          handleDisableButton={handleDisableButtonAccount()}
          titleButton="Send Invitation"
          styleRoot={{
            marginTop: 20,
          }}
        />
      </div>
    )
  }

  return (
    <ModalCustom
      handleChangeButton={() => onCloseModal}
      label="Invite Member"
      widthModal={490}
      bodyChildren={renderBody()}
    />
  )
}
